import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  MagnifyingGlass,
  Copy,
  Trash,
  Plus,
  CheckCircle,
  WarningCircle,
  ClockCounterClockwise,
  Shield,
  ArrowsClockwise,
  ClipboardText,
  Database,
  X,
  Columns,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Toaster, toast } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SAP hard-caps EngineeringChangeOrder ID at 12 chars - matches
// sap_eco_client.ECO_ID_MAX_LENGTH on the backend (Sep 25 2026, user's
// ask: catch this INSTANTLY client-side, no wasted SAP round-trip).
const ECO_ID_MAX_LENGTH = 12;

const STATUS_BADGE = {
  draft: "bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]",
  validation_pending: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  ready_to_create: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
  creating_in_sap: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  created: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  failed: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
};

const STATUS_LABEL = {
  draft: "Draft",
  validation_pending: "Validation Pending",
  ready_to_create: "Ready to Create",
  creating_in_sap: "Creating in SAP...",
  created: "Created",
  failed: "Failed",
};

const CREATABLE_STATUSES = ["ready_to_create", "failed"];

const StatusBadge = ({ status, testId }) => (
  <Badge variant="outline" className={`rounded text-[11px] ${STATUS_BADGE[status] || ""}`} data-testid={testId}>
    {STATUS_LABEL[status] || status}
  </Badge>
);

// Oct 7 2026, user's explicit ask: "we don't need draft in the copy BOM -
// remove current draft and do not save for the next copy". The Drafts
// sidebar (saved list + New + per-row delete) is gone entirely: a copy is
// only the thing you are editing right now. The server-side record stays
// for the few minutes it takes to Validate/Create-in-SAP, is discarded the
// moment a new copy is pulled, and otherwise self-deletes 1 h after its
// last edit (bom_copy_service.DRAFT_TTL_HOURS).

function ProductPicker({ value, idx, slot, onSelect }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => setQuery(value || ""), [value]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) { setSuggestions([]); return undefined; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/bom-copy/products/search`, { params: { q: query, limit: 8 } });
        setSuggestions(data);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Oct 8 2026, user's report ("ITEM 2572-8 unable to fetch from SAP with
  // name and unit"): `component_master` holds plenty of product IDs with
  // NO description and NO base_uom (the autosuggest lists them as bare
  // codes - 2572-1 ... 2572-8 are all like that), and this picker treated
  // "appears in the suggestion list" as "fully resolved", so it took the
  // local shortcut and never fell through to the live SAP read, which
  // DOES know them (/bom-copy/products/lookup/2572-8 returns "Flat Small
  // Support", EA). A suggestion is only trusted when it actually carries
  // both a description and a UOM.
  const isComplete = (s) => !!(s && s.description && s.uom);

  const resolveFromSap = async (typed) => {
    onSelect({ product_id: typed, description: "Looking up\u2026" });
    setLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/bom-copy/products/lookup/${encodeURIComponent(typed)}`);
      onSelect({ product_id: typed, description: data.description || "", uom: data.uom || "", category: data.category || "" });
      toast.success(`Fetched "${typed}" from ${data.source === "sap" ? "SAP" : "cache"}`, { description: data.uom ? `UOM: ${data.uom}` : "No UOM found - enter manually" });
    } catch (err) {
      onSelect({ product_id: typed, description: "" });
      toast.error(`"${typed}" not found`, { description: err?.response?.data?.detail || "Could not resolve this product" });
    } finally {
      setLookingUp(false);
    }
  };

  const pick = async (s) => {
    onSelect({ product_id: s.product_id, description: s.description || "", uom: s.uom || "", category: s.category || "" });
    setQuery(s.product_id);
    setShowSuggestions(false);
    if (!isComplete(s)) await resolveFromSap(s.product_id);
  };

  const handleBlur = async () => {
    setShowSuggestions(false);
    const typed = query.trim();
    if (!typed || typed === value) return;
    const known = suggestions.find((s) => s.product_id === typed);
    if (isComplete(known)) {
      onSelect({ product_id: typed, description: known.description, uom: known.uom, category: known.category || "" });
      return;
    }
    await resolveFromSap(typed);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={handleBlur}
        disabled={lookingUp}
        placeholder={lookingUp ? "Looking up in SAP..." : "Product ID"}
        className="h-7 text-[12px] rounded-sm font-mono"
        data-testid={`bom-copy-line-product-${slot}-${idx}`}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-30 mt-1 w-64 bg-white border border-[#D0D5DD] rounded-sm shadow-lg max-h-52 overflow-y-auto" data-testid={`bom-copy-line-product-suggestions-${slot}-${idx}`}>
          {suggestions.map((s) => (
            <button
              key={s.product_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[#F0F4F8] border-b border-[#EAECF0] last:border-0"
              data-testid={`bom-copy-line-product-suggestion-${slot}-${idx}-${s.product_id}`}
            >
              <div className="font-semibold text-[#101828]">
                {s.product_id} {s.uom && <span className="text-[#667085] font-normal">({s.uom})</span>}
              </div>
              {s.description && <div className="text-[#667085] truncate">{s.description}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Oct 8 2026: Group ID ordering for the lines table (see sortLinesByGroup).
// Numeric-aware, so "100" lands after "20" rather than before it; a blank
// or non-numeric group sorts last instead of jumping to the top.
const compareByGroupId = (a, b) => {
  const ra = (a.group_id || "").trim();
  const rb = (b.group_id || "").trim();
  const aNum = ra !== "" && Number.isFinite(Number(ra));
  const bNum = rb !== "" && Number.isFinite(Number(rb));
  if (aNum && bNum) return Number(ra) - Number(rb);
  if (aNum !== bNum) return aNum ? -1 : 1;
  return ra.localeCompare(rb);
};

function LineRow({ line, idx, slot, onChange, onRemove, onGroupBlur }) {
  return (
    <tr className={idx % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`bom-copy-line-row-${slot}-${idx}`}>
      <td className="border border-[#D0D5DD] p-1">
        <Input value={line.group_id} onChange={(e) => onChange(idx, { group_id: e.target.value })} onBlur={onGroupBlur} className="h-7 text-[12px] rounded-sm" data-testid={`bom-copy-line-group-${slot}-${idx}`} />
      </td>
      <td className="border border-[#D0D5DD] p-1">
        <Input value={line.item_id} onChange={(e) => onChange(idx, { item_id: e.target.value })} className="h-7 text-[12px] rounded-sm" data-testid={`bom-copy-line-item-${slot}-${idx}`} />
      </td>
      <td className="border border-[#D0D5DD] p-1">
        <ProductPicker value={line.product_id} idx={idx} slot={slot} onSelect={(patch) => onChange(idx, patch)} />
      </td>
      <td className="border border-[#D0D5DD] px-2 py-1 text-[12px] text-[#667085]" data-testid={`bom-copy-line-category-${slot}-${idx}`}>{line.category || "\u2014"}</td>
      <td className="border border-[#D0D5DD] px-2 py-1 text-[12px] text-[#667085]">{line.description || "\u2014"}</td>
      <td className="border border-[#D0D5DD] p-1">
        <Input type="number" value={line.quantity} onChange={(e) => onChange(idx, { quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-[12px] rounded-sm w-24" data-testid={`bom-copy-line-quantity-${slot}-${idx}`} />
      </td>
      <td className="border border-[#D0D5DD] p-1">
        <Input value={line.uom} onChange={(e) => onChange(idx, { uom: e.target.value })} className="h-7 text-[12px] rounded-sm w-16" data-testid={`bom-copy-line-uom-${slot}-${idx}`} />
      </td>
      <td className="border border-[#D0D5DD] p-1 text-center">
        <button type="button" onClick={() => onRemove(idx)} className="text-[#B42318] hover:text-[#7A271A]" data-testid={`bom-copy-line-remove-${slot}-${idx}`}>
          <Trash size={14} />
        </button>
      </td>
    </tr>
  );
}

function ComparisonView({ sourceLines, newLines, slot }) {
  const maxLen = Math.max(sourceLines.length, newLines.length);
  const rows = [];
  for (let i = 0; i < maxLen; i++) {
    rows.push({ src: sourceLines[i], dst: newLines[i] });
  }
  const changed = (a, b) => JSON.stringify(a || {}) !== JSON.stringify(b || {});
  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid={`bom-copy-comparison-view-${slot}`}>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th colSpan={4} className="bg-[#F2F4F7] border border-[#D0D5DD] p-1.5 text-[11px] font-bold uppercase text-[#344054]">Source BOM</th>
            <th colSpan={4} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-[11px] font-bold uppercase text-[#344054]">New BOM</th>
          </tr>
          <tr>
            {["Line Item", "Product", "Qty", "Group ID", "Line Item", "Product", "Qty", "Group ID"].map((h, i) => (
              <th key={i} className="bg-[#F9FAFB] border border-[#D0D5DD] p-1 text-[10px] uppercase font-bold text-[#667085]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`bom-copy-compare-row-${slot}-${i}`}>
              <td className="border border-[#D0D5DD] px-2 py-1 text-[12px]">{r.src?.item_id ?? "\u2014"}</td>
              <td className="border border-[#D0D5DD] px-2 py-1 text-[12px] font-mono">{r.src?.product_id ?? "\u2014"}</td>
              <td className="border border-[#D0D5DD] px-2 py-1 text-[12px]">{r.src?.quantity ?? "\u2014"}</td>
              <td className="border border-[#D0D5DD] px-2 py-1 text-[12px]">{r.src?.group_id ?? "\u2014"}</td>
              <td className={`border border-[#D0D5DD] px-2 py-1 text-[12px] ${changed(r.src?.item_id, r.dst?.item_id) ? "bg-[#FFFAEB] text-[#B54708] font-semibold" : ""}`}>{r.dst?.item_id ?? "\u2014"}</td>
              <td className={`border border-[#D0D5DD] px-2 py-1 text-[12px] font-mono ${changed(r.src?.product_id, r.dst?.product_id) ? "bg-[#FFFAEB] text-[#B54708] font-semibold" : ""}`}>{r.dst?.product_id ?? "\u2014"}</td>
              <td className={`border border-[#D0D5DD] px-2 py-1 text-[12px] ${changed(r.src?.quantity, r.dst?.quantity) ? "bg-[#FFFAEB] text-[#B54708] font-semibold" : ""}`}>{r.dst?.quantity ?? "\u2014"}</td>
              <td className={`border border-[#D0D5DD] px-2 py-1 text-[12px] ${changed(r.src?.group_id, r.dst?.group_id) ? "bg-[#FFFAEB] text-[#B54708] font-semibold" : ""}`}>{r.dst?.group_id ?? "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Sep 2026, user's explicit ask: "if output item have multi active bom
// give me option to expose both as table" - one output product can have
// several simultaneously-active SAP BOM variants; this panel renders ONE
// variant's full editable draft (identical to the old single-draft view)
// but is now reusable so 2 can render side-by-side (see BomCopyPage below).
function VariantPanel({
  slot, panelLabel, draft, uiState, onPatch, onUpdateLine, onRemoveLine, onAddLine, onSortLines,
  onSave, onValidate, onCreateRequest, onToggleComparison, onToggleAudit, onCloseSecondary,
}) {
  const canEdit = draft && !["creating_in_sap", "created"].includes(draft.status);
  const { saving, validating, creating, showComparison, showAudit, audit } = uiState;

  return (
    <div className="space-y-3" data-testid={`bom-copy-variant-panel-${slot}`}>
      {panelLabel && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#175CD3]" data-testid={`bom-copy-variant-label-${slot}`}>{panelLabel}</span>
          {onCloseSecondary && (
            <button type="button" onClick={onCloseSecondary} className="text-[#98A2B3] hover:text-[#B42318] flex items-center gap-1 text-[11px]" data-testid="bom-copy-close-secondary-button">
              <X size={12} /> Remove from view
            </button>
          )}
        </div>
      )}

      <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex flex-wrap items-center gap-4" data-testid={`bom-copy-draft-header-${slot}`}>
        <div>
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">Source BOM</div>
          <div className="text-sm font-mono text-[#101828]" data-testid={`bom-copy-source-bom-id-${slot}`}>{draft.source_bom_id}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">New BOM ID (auto-generated)</div>
          <div className="text-sm font-mono text-[#004B87] font-bold" data-testid={`bom-copy-new-bom-id-${slot}`}>{draft.new_bom_id}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">
            ECO ID (will be created in SAP)
            <span className={`ml-1 font-mono ${(draft.eco_id || "").length > ECO_ID_MAX_LENGTH ? "text-[#B42318]" : "text-[#98A2B3]"}`} data-testid={`bom-copy-eco-id-char-count-${slot}`}>
              {(draft.eco_id || "").length}/{ECO_ID_MAX_LENGTH}
            </span>
          </div>
          <Input
            disabled={!canEdit}
            value={draft.eco_id || ""}
            onChange={(e) => onPatch({ eco_id: e.target.value })}
            className={`h-7 text-[12px] rounded-sm w-36 font-mono ${(draft.eco_id || "").length > ECO_ID_MAX_LENGTH ? "border-[#FDA29B] focus-visible:ring-[#FDA29B]" : ""}`}
            placeholder="e.g. MAZ7272-RA6"
            data-testid={`bom-copy-eco-id-input-${slot}`}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">Output Product</div>
          <div className="text-sm text-[#101828]">{draft.output_product_id}</div>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <div className="text-[10px] uppercase text-[#98A2B3] font-bold">Output Qty</div>
            <Input
              type="number"
              disabled={!canEdit}
              value={draft.output_quantity}
              onChange={(e) => onPatch({ output_quantity: parseFloat(e.target.value) || 0 })}
              className="h-7 text-[12px] rounded-sm w-24"
              data-testid={`bom-copy-output-quantity-input-${slot}`}
            />
          </div>
          <div>
            <div className="text-[10px] uppercase text-[#98A2B3] font-bold">UOM</div>
            <Input
              disabled={!canEdit}
              value={draft.output_uom}
              onChange={(e) => onPatch({ output_uom: e.target.value })}
              className="h-7 text-[12px] rounded-sm w-16"
              data-testid={`bom-copy-output-uom-input-${slot}`}
            />
          </div>
        </div>
        <div className="ml-auto"><StatusBadge status={draft.status} testId={`bom-copy-draft-header-status-${slot}`} /></div>
      </div>

      <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex flex-wrap gap-4" data-testid={`bom-copy-description-remark-panel-${slot}`}>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">BOM Description (will be created in SAP)</div>
          <Input
            disabled={!canEdit}
            value={draft.bom_description || ""}
            onChange={(e) => onPatch({ bom_description: e.target.value })}
            className="h-8 text-[13px] rounded-sm mt-1"
            placeholder="e.g. Wall Plate Operation HRPO2.8X171"
            data-testid={`bom-copy-description-input-${slot}`}
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase text-[#98A2B3] font-bold">Remark (written into SAP Variant Details, stamped with your name + time)</div>
          <Input
            disabled={!canEdit}
            value={draft.remark || ""}
            onChange={(e) => onPatch({ remark: e.target.value })}
            className="h-8 text-[13px] rounded-sm mt-1"
            placeholder="Optional - any note for this BOM copy"
            data-testid={`bom-copy-remark-input-${slot}`}
          />
        </div>
      </div>

      {/* Oct 8 2026, user's report: a line sitting in an unreleased ECO is
          not part of SAP's effective BOM, so it is no longer copied - but
          never drop it silently, or the copy looks like it lost a line. */}
      {draft.excluded_lines?.length > 0 && (
        <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-3 space-y-1" data-testid={`bom-copy-excluded-lines-banner-${slot}`}>
          <div className="text-[13px] font-semibold text-[#B54708]">
            {draft.excluded_lines.length} line{draft.excluded_lines.length === 1 ? "" : "s"} in {draft.source_bom_id} {draft.excluded_lines.length === 1 ? "was" : "were"} not copied
          </div>
          {draft.excluded_lines.map((l) => (
            <div key={`${l.group_id}-${l.item_id}-${l.product_id}`} className="text-xs text-[#93370D]">
              Line {l.item_id} · <span className="font-semibold">{l.product_id}</span>
              {l.description ? ` (${l.description})` : ""} - Engineering Change Order{" "}
              <span className="font-mono">{l.eco_id || "?"}</span> is not released in SAP, so SAP does not
              count it as part of this BOM. Release that ECO in SAP and pull the copy again to include it.
            </div>
          ))}
        </div>
      )}


      {draft.status === "created" && draft.sap_result && (
        <div className="bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm p-3 flex items-start gap-2 text-[#027A48]" data-testid={`bom-copy-success-banner-${slot}`}>
          <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
          <span className="text-sm font-semibold">
            Created BOM {draft.sap_result.new_bom_id} under ECO {draft.sap_result.eco_id} (date {draft.sap_result.eco_date})
            {draft.sap_result.recovered_from_error && (
              <span className="block font-normal text-[12px] text-[#027A48]/80 mt-1" data-testid={`bom-copy-recovered-note-${slot}`}>
                SAP replied with an error ({draft.sap_result.recovered_from_error}) but the BOM is in SAP - checked and confirmed. Nothing to retry.
              </span>
            )}
          </span>
        </div>
      )}
      {draft.status === "failed" && draft.sap_result?.error && (
        <div className="bg-[#FEF3F2] border border-[#FECDCA] rounded-sm p-3 flex items-start gap-2 text-[#912018]" data-testid={`bom-copy-error-banner-${slot}`}>
          <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
          <span className="text-sm">{draft.sap_result.error}</span>
        </div>
      )}
      {draft.status === "validation_pending" && draft.validation_errors?.length > 0 && (
        <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-3 text-[#B54708]" data-testid={`bom-copy-validation-errors-${slot}`}>
          <p className="text-sm font-bold mb-1">{draft.validation_errors.length} validation error(s):</p>
          <ul className="text-[12px] list-disc pl-4 space-y-0.5">
            {draft.validation_errors.map((err, i) => <li key={i} data-testid={`bom-copy-validation-error-${slot}-${i}`}>{err}</li>)}
          </ul>
        </div>
      )}
      {draft.status === "ready_to_create" && draft.pending_eco_id && (
        <div className="bg-[#F0F4FF] border border-[#C7D7FE] rounded-sm p-3 text-[#3538CD]" data-testid={`bom-copy-eco-preview-banner-${slot}`}>
          <p className="text-sm font-bold">
            Validated. ECO <span className="font-mono">{draft.pending_eco_id}</span> will be created and assigned to
            all {draft.lines.length} line item(s) across {new Set(draft.lines.map((l) => l.group_id)).size} group(s),
            with Valid From date <span className="font-mono">{draft.pending_eco_date}</span> (today &minus; 30 days).
          </p>
        </div>
      )}

      <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid={`bom-copy-lines-table-container-${slot}`}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["Group ID", "Line Item", "Input Product", "Product Category", "Description", "Quantity", "UOM", ""].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-[11px] font-bold uppercase text-[#344054]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draft.lines.map((line, idx) => (
              <LineRow key={idx} line={line} idx={idx} slot={slot} onChange={canEdit ? onUpdateLine : () => {}} onRemove={canEdit ? onRemoveLine : () => {}} onGroupBlur={canEdit ? onSortLines : () => {}} />
            ))}
            {draft.lines.length === 0 && (
              <tr><td colSpan={8} className="border border-[#D0D5DD] text-center py-6 text-[13px] text-[#475467]">No lines - click "Add Line" below</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onAddLine} className="h-8 text-xs rounded-sm" data-testid={`bom-copy-add-line-button-${slot}`}>
            <Plus size={13} className="mr-1.5" /> Add Line
          </Button>
        )}
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onSave} disabled={saving} className="h-8 text-xs rounded-sm" data-testid={`bom-copy-save-draft-button-${slot}`}>
            {saving ? "Saving..." : "Save Draft"}
          </Button>
        )}
        {canEdit && (
          <Button size="sm" onClick={onValidate} disabled={validating} className="h-8 bg-[#5925DC] hover:bg-[#4A1FB8] text-white text-xs rounded-sm" data-testid={`bom-copy-validate-button-${slot}`}>
            {validating ? "Validating..." : "Validate"}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onToggleComparison} className="h-8 text-xs rounded-sm" data-testid={`bom-copy-toggle-comparison-button-${slot}`}>
          {showComparison ? "Hide Comparison" : "Show Comparison"}
        </Button>
        <Button variant="outline" size="sm" onClick={onToggleAudit} className="h-8 text-xs rounded-sm" data-testid={`bom-copy-toggle-audit-button-${slot}`}>
          <ClockCounterClockwise size={13} className="mr-1.5" /> {showAudit ? "Hide Audit Trail" : "Audit Trail"}
        </Button>
        {CREATABLE_STATUSES.includes(draft.status) && (
          <Button
            onClick={onCreateRequest}
            disabled={creating}
            className="h-8 bg-[#B42318] hover:bg-[#912018] text-white text-xs rounded-sm ml-auto font-bold"
            data-testid={`bom-copy-create-in-sap-button-${slot}`}
          >
            <ArrowsClockwise size={13} className={`mr-1.5 ${creating ? "animate-spin" : ""}`} />
            {creating ? "Creating in SAP..." : draft.status === "failed" ? "Retry: Create New BOM in SAP" : "Create New BOM in SAP"}
          </Button>
        )}
      </div>

      {showComparison && <ComparisonView sourceLines={draft.source_lines} newLines={draft.lines} slot={slot} />}

      {showAudit && (
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-3" data-testid={`bom-copy-audit-panel-${slot}`}>
          <p className="text-xs font-bold text-[#344054] uppercase tracking-wide mb-2">Audit Trail</p>
          <ul className="space-y-2">
            {audit.map((entry, i) => (
              <li key={i} className="text-[12px] border-l-2 border-[#D0D5DD] pl-2" data-testid={`bom-copy-audit-entry-${slot}-${i}`}>
                <span className="font-semibold text-[#101828]">{entry.action}</span>{" "}
                <span className="text-[#667085]">by {entry.user} at {new Date(entry.timestamp).toLocaleString("en-IN")}</span>
              </li>
            ))}
            {audit.length === 0 && <li className="text-[12px] text-[#98A2B3]">No entries yet.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

const EMPTY_UI = { saving: false, validating: false, creating: false, showComparison: false, showAudit: false, audit: [] };

export default function BomCopyPage() {
  const [productId, setProductId] = useState("");
  const [searching, setSearching] = useState(false);
  const [primaryDraft, setPrimaryDraft] = useState(null);
  const [secondaryDraft, setSecondaryDraft] = useState(null);
  const [ui, setUi] = useState({ primary: EMPTY_UI, secondary: EMPTY_UI });
  const [confirmTarget, setConfirmTarget] = useState(null); // null | "primary" | "secondary" | "both"
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [picked, setPicked] = useState([]);

  const openPairKey = `${primaryDraft?.source_bom_id || ""}|${secondaryDraft?.source_bom_id || ""}`;
  useEffect(() => {
    setPicked([primaryDraft?.source_bom_id, secondaryDraft?.source_bom_id].filter(Boolean));
  }, [openPairKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const draftOf = (slot) => (slot === "primary" ? primaryDraft : secondaryDraft);
  const setDraftOf = (slot, updater) => (slot === "primary" ? setPrimaryDraft(updater) : setSecondaryDraft(updater));
  const patchUi = (slot, patch) => setUi((prev) => ({ ...prev, [slot]: { ...prev[slot], ...patch } }));

  // Throws away the server-side record of a copy that is being replaced, so
  // nothing is left to resume. Only for unfinished ones - anything that
  // reached SAP ('created') or is mid-post ('creating_in_sap') is a real
  // BOM/ECO now and the backend refuses to delete it anyway. Best effort:
  // the 1 h TTL is the backstop, so a failure here is never worth a toast.
  const discardUnfinished = async (candidates) => {
    const ids = candidates
      .filter((d) => d && !["creating_in_sap", "created"].includes(d.status))
      .map((d) => d._id);
    await Promise.all(ids.map((id) => axios.delete(`${API}/bom-copy/drafts/${id}`).catch(() => null)));
  };

  // Sep 2026, user's explicit ask: "if output item have multi active bom
  // give me option to expose both as table" - when the SAP output
  // product has >1 simultaneously-active BOM, auto-load a 2nd variant
  // as a separate, independently-editable draft right next to the
  // first, so nothing has to be re-copied/re-typed for the 2nd one.
  const loadAsSecondary = async (bomId) => {
    try {
      await discardUnfinished([secondaryDraft]);
      const { data } = await axios.post(`${API}/bom-copy/drafts`, { product_id: bomId });
      setSecondaryDraft(data);
      setUi((prev) => ({ ...prev, secondary: EMPTY_UI }));
      toast.info(`Loaded 2nd variant ${data.source_bom_id} side-by-side`, { description: `New BOM ${data.new_bom_id}` });
    } catch (err) {
      toast.error("Could not load 2nd variant", { description: err?.response?.data?.detail });
    }
  };

  const handleCreateCopy = async (overrideId) => {
    const searchId = (typeof overrideId === "string" ? overrideId : productId).trim();
    if (!searchId) return;
    setSearching(true);
    await discardUnfinished([primaryDraft, secondaryDraft]);
    setSecondaryDraft(null);
    try {
      const { data } = await axios.post(`${API}/bom-copy/drafts`, { product_id: searchId });
      setPrimaryDraft(data);
      setUi({ primary: EMPTY_UI, secondary: EMPTY_UI });
      toast.success("Copy pulled from SAP", { description: `Copying ${data.source_bom_id} \u2192 ${data.new_bom_id}` });
      const other = (data.alternate_boms || []).find((a) => a.bom_id !== data.source_bom_id);
      if (other) {
        toast.info(`${data.alternate_boms.length} active BOMs found for this product`, { description: `Loading ${other.bom_id} side-by-side...` });
        await loadAsSecondary(other.bom_id);
      }
    } catch (err) {
      toast.error("Could not pull BOM from SAP", { description: err?.response?.data?.detail || "Lookup failed" });
    } finally {
      setSearching(false);
    }
  };

  // Sep 25 2026, root cause of "Remark ends up blank in SAP" bug: the
  // Remark/Description/Quantity/UOM/Lines inputs stay editable at
  // 'ready_to_create'/'failed' (canEdit), but `createInSap` posts
  // straight to /create-in-sap with NO save first (must NOT auto-save,
  // since a save would flip status back to 'draft' and correctly block
  // the Create button until re-Validated) - so any edit typed AFTER
  // clicking Validate was silently discarded, and the STALE
  // 'ready_to_create' status kept the Create button visible/clickable
  // as if nothing changed. Every field editor now goes through this
  // helper, which invalidates the client-side status back to 'draft'
  // (hiding Create, same as the STO page's own "reset on edit" pattern)
  // the instant anything changes past a Validate - forcing a fresh
  // Validate (which DOES save) before Create can run again.
  const patchDraft = (slot, patch) => {
    setDraftOf(slot, (prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      if (prev.status === "ready_to_create" || prev.status === "failed") {
        next.status = "draft";
        next.pending_eco_id = null;
        next.pending_eco_date = null;
      }
      return next;
    });
  };

  const updateLine = (slot, idx, patch) => {
    patchDraft(slot, (prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...prev, lines };
    });
  };

  const removeLine = (slot, idx) => {
    patchDraft(slot, (prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  };

  // Oct 8 2026, user's explicit ask: "while creating copy BOM and change
  // group ID, you need to auto set item order based on the group ID" - the
  // rows regroup themselves the moment the Group ID box loses focus (never
  // mid-keystroke, or typing "40" would reorder at "4" first). The typed
  // LINE ITEM numbers are kept exactly as they are (user's explicit
  // choice - no renumbering), and the sort is stable, so a group's own
  // internal order is untouched and only the blocks move. Rows are keyed
  // by position, so focus is dropped when something actually moved -
  // otherwise the next keystroke would land in a different line's cell.
  const sortLinesByGroup = (slot) => {
    const current = slot === "primary" ? primaryDraft : secondaryDraft;
    if (!current) return;
    const lines = [...current.lines].sort(compareByGroupId);
    if (lines.every((l, i) => l === current.lines[i])) return;
    document.activeElement?.blur();
    toast.info("Lines regrouped by Group ID");
    patchDraft(slot, { lines });
  };

  const addLine = (slot) => {
    patchDraft(slot, (prev) => ({
      ...prev,
      lines: [...prev.lines, { group_id: "", item_id: "", product_id: "", description: "", category: "", quantity: 1, uom: prev.output_uom || "EA" }],
    }));
  };

  const refreshItemCache = async () => {
    setRefreshingCache(true);
    try {
      const { data } = await axios.post(`${API}/inventory`);
      const jobId = data.job_id;
      let failures = 0;
      const poll = async () => {
        try {
          const { data: job } = await axios.get(`${API}/inventory/${jobId}`);
          failures = 0;
          if (job.status === "running") {
            setTimeout(poll, 2000);
          } else if (job.status === "done") {
            toast.success("Item cache refreshed from SAP", { description: `${job.result.items.length} items` });
            setRefreshingCache(false);
          } else {
            toast.error("Refresh failed", { description: job.error });
            setRefreshingCache(false);
          }
        } catch (err) {
          failures += 1;
          if (failures <= 5) setTimeout(poll, 2000);
          else { toast.error("Refresh failed", { description: err.message }); setRefreshingCache(false); }
        }
      };
      poll();
    } catch (err) {
      toast.error("Could not start refresh", { description: err?.response?.data?.detail });
      setRefreshingCache(false);
    }
  };

  const saveDraft = async (slot) => {
    const draft = draftOf(slot);
    patchUi(slot, { saving: true });
    try {
      const { data } = await axios.put(`${API}/bom-copy/drafts/${draft._id}`, {
        lines: draft.lines,
        output_quantity: draft.output_quantity,
        output_uom: draft.output_uom,
        bom_description: draft.bom_description || "",
        remark: draft.remark || "",
        eco_id: draft.eco_id || "",
      });
      setDraftOf(slot, data);
      toast.success("Changes saved");
      return data;
    } catch (err) {
      toast.error("Could not save draft", { description: err?.response?.data?.detail });
      throw err;
    } finally {
      patchUi(slot, { saving: false });
    }
  };

  const validate = async (slot) => {
    try {
      await saveDraft(slot);
    } catch {
      return;
    }
    patchUi(slot, { validating: true });
    try {
      const draft = draftOf(slot);
      const { data } = await axios.post(`${API}/bom-copy/drafts/${draft._id}/validate`);
      setDraftOf(slot, data);
      if (data.status === "ready_to_create") {
        toast.success("Validation passed", { description: `${data.new_bom_id} is ready to create in SAP` });
      } else {
        toast.error(`${data.validation_errors.length} validation error(s) on ${data.new_bom_id}`, { description: data.validation_errors[0] });
      }
    } catch (err) {
      toast.error("Validation failed", { description: err?.response?.data?.detail });
    } finally {
      patchUi(slot, { validating: false });
    }
  };

  // Both variants validate/create fully independently - if one fails,
  // the other's own success/failure is unaffected and reported on its
  // own panel (user's explicit choice).
  const validateBoth = async () => {
    await validate("primary");
    if (secondaryDraft) await validate("secondary");
  };

  // 19 Oct 2026, user's ask: with more than 2 active BOMs, tick WHICH two to
  // copy together (the first two come pre-ticked, matching what loads by
  // default). Only two fit side by side, so a third tick asks for one back.
  const togglePick = (bomId) => {
    setPicked((prev) => {
      if (prev.includes(bomId)) return prev.filter((id) => id !== bomId);
      if (prev.length >= 2) {
        toast.info("Only 2 variants fit side-by-side", { description: "Untick one first, then tick this." });
        return prev;
      }
      return [...prev, bomId];
    });
  };

  const openPickedPair = async () => {
    if (picked.length !== 2) return;
    setSearching(true);
    await discardUnfinished([primaryDraft, secondaryDraft]);
    setSecondaryDraft(null);
    try {
      // Sequential on purpose: the 2nd draft has to SEE the BOM number the 1st
      // just reserved, or both would be offered the same one.
      const { data: first } = await axios.post(`${API}/bom-copy/drafts`, { product_id: picked[0] });
      setPrimaryDraft(first);
      setUi({ primary: EMPTY_UI, secondary: EMPTY_UI });
      const { data: second } = await axios.post(`${API}/bom-copy/drafts`, { product_id: picked[1] });
      setSecondaryDraft(second);
      toast.success("Opened side-by-side", { description: `${first.new_bom_id} and ${second.new_bom_id}` });
    } catch (err) {
      toast.error("Could not open the selected pair", { description: err?.response?.data?.detail });
    } finally {
      setSearching(false);
    }
  };

  const createInSap = async (slot) => {
    const draft = draftOf(slot);
    patchUi(slot, { creating: true });
    try {
      const { data } = await axios.post(`${API}/bom-copy/drafts/${draft._id}/create-in-sap`);
      setDraftOf(slot, data);
      toast.success(`Created in SAP: ${data.sap_result.new_bom_id}`, {
        description: `ECO ${data.sap_result.eco_id} (date ${data.sap_result.eco_date})`,
      });
    } catch (err) {
      toast.error(`SAP creation failed for ${draft.new_bom_id}`, { description: err?.response?.data?.detail, duration: 15000 });
      const { data } = await axios.get(`${API}/bom-copy/drafts/${draft._id}`).catch(() => ({ data: null }));
      if (data) setDraftOf(slot, data);
    } finally {
      patchUi(slot, { creating: false });
    }
  };

  // 19 Oct 2026, root cause of "HTTP 500 while creating 2 BOMs at the same
  // time": these two used to fire together, so SAP got two writes against the
  // same output product at once and neither could see the ECO/BOM number the
  // other had just taken. Strictly one after the other now - the backend holds
  // a per-product lock as the real guard, this keeps the panels honest too.
  const handleConfirmCreate = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (target === "both") {
      for (const slot of ["primary", "secondary"]) {
        const d = draftOf(slot);
        if (d && CREATABLE_STATUSES.includes(d.status)) await createInSap(slot);
      }
    } else if (target) {
      await createInSap(target);
    }
  };

  const loadAudit = async (slot) => {
    const isOpen = ui[slot].showAudit;
    patchUi(slot, { showAudit: !isOpen });
    if (isOpen) return;
    try {
      const draft = draftOf(slot);
      const { data } = await axios.get(`${API}/bom-copy/drafts/${draft._id}/audit`);
      patchUi(slot, { audit: data.entries || [] });
    } catch {
      patchUi(slot, { audit: [] });
    }
  };

  const eligibleForBoth = ["primary", "secondary"].filter((slot) => {
    const d = draftOf(slot);
    return d && CREATABLE_STATUSES.includes(d.status);
  });

  const confirmInfo = (() => {
    if (confirmTarget === "both") {
      return {
        title: `Create ${eligibleForBoth.length} BOM(s) in SAP?`,
        boms: eligibleForBoth.map((s) => draftOf(s).new_bom_id),
      };
    }
    if (confirmTarget) {
      return { title: "Create this BOM in SAP?", boms: [draftOf(confirmTarget)?.new_bom_id] };
    }
    return null;
  })();

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Copy Existing BOM</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="bg-white border-b border-[#D0D5DD] p-2 flex items-center gap-2 shrink-0" data-testid="bom-copy-search-bar">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
          <Input
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            placeholder="Output Product ID e.g. MAZ42117272-WP"
            className="h-8 pl-7 w-80 text-[13px] rounded-sm"
            data-testid="bom-copy-product-id-input"
            onKeyDown={(e) => e.key === "Enter" && handleCreateCopy()}
          />
        </div>
        <Button
          onClick={handleCreateCopy}
          disabled={searching || !productId.trim()}
          className="h-8 bg-[#004B87] hover:bg-[#003A6A] text-white rounded-sm px-4 text-[13px] font-bold"
          data-testid="bom-copy-create-copy-button"
        >
          <Copy size={14} className="mr-1.5" />
          {searching ? "Pulling from SAP..." : "Create Copy"}
        </Button>
        <p className="text-[11px] text-[#98A2B3] ml-2">Pulls the current SAP BOM, creates a separate editable draft. Source BOM is never touched.</p>
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshItemCache}
            disabled={refreshingCache}
            className="h-8 text-xs rounded-sm"
            data-testid="bom-copy-refresh-item-cache-button"
            title="Refresh the Product ID/description/UOM cache used by the Input Product picker below, from live SAP"
          >
            <Database size={13} className={`mr-1.5 ${refreshingCache ? "animate-pulse" : ""}`} />
            {refreshingCache ? "Refreshing from SAP..." : "Refresh Item Cache"}
          </Button>
        </div>
      </div>

      <main className="flex-1 overflow-auto p-4">
        <div className="min-w-0 space-y-3">
          {!primaryDraft && (
            <div className="border border-dashed border-[#D0D5DD] rounded-sm py-16 flex flex-col items-center gap-3 text-[#98A2B3] bg-white" data-testid="bom-copy-empty-state">
              <ClipboardText size={28} />
              <p className="text-[13px]">Enter an Output Product ID above to get started.</p>
            </div>
          )}

          {primaryDraft && (
            <>
              {primaryDraft.alternate_boms?.length > 1 && (
                <div className="bg-[#EFF8FF] border border-[#B2DDFF] rounded-sm p-3 text-[#175CD3]" data-testid="bom-copy-alternates-banner">
                  <p className="text-sm font-bold mb-1">
                    This output product has {primaryDraft.alternate_boms.length} active BOMs in SAP.
                    {secondaryDraft
                      ? ` Editing side-by-side: ${primaryDraft.source_bom_id} (Variant 1) and ${secondaryDraft.source_bom_id} (Variant 2).`
                      : ` Currently copying from ${primaryDraft.source_bom_id}.`}
                  </p>
                  {primaryDraft.alternate_boms.length > 2 && (
                    <p className="text-[12px] mb-1.5 text-[#175CD3]/80" data-testid="bom-copy-pick-hint">
                      Tick the two you want to copy together, then open them side-by-side.
                    </p>
                  )}
                  <ul className="text-[12px] space-y-1">
                    {primaryDraft.alternate_boms.map((alt) => {
                      const alreadyShown = alt.bom_id === primaryDraft.source_bom_id || alt.bom_id === secondaryDraft?.source_bom_id;
                      const manyVariants = primaryDraft.alternate_boms.length > 2;
                      return (
                        <li key={alt.bom_id} className="flex items-center gap-2 flex-wrap" data-testid={`bom-copy-alternate-${alt.bom_id}`}>
                          {manyVariants && (
                            <Checkbox
                              checked={picked.includes(alt.bom_id)}
                              onCheckedChange={() => togglePick(alt.bom_id)}
                              className="h-3.5 w-3.5"
                              data-testid={`bom-copy-pick-${alt.bom_id}`}
                            />
                          )}
                          <span className="font-mono font-semibold">{alt.bom_id}</span>
                          {alt.sample_item_description && <span className="text-[#475467]">e.g. {alt.sample_item_description}</span>}
                          {!alreadyShown && !manyVariants && (
                            <button type="button" className="underline font-semibold" onClick={() => loadAsSecondary(alt.bom_id)} data-testid={`bom-copy-use-alternate-${alt.bom_id}`}>
                              {secondaryDraft ? "Replace Variant 2 with this" : "Load side-by-side"}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {primaryDraft.alternate_boms.length > 2 && (
                    <Button
                      size="sm"
                      onClick={openPickedPair}
                      disabled={searching || picked.length !== 2 || picked.every((id) => id === primaryDraft.source_bom_id || id === secondaryDraft?.source_bom_id)}
                      className="h-7 mt-2 bg-[#175CD3] hover:bg-[#134EA8] text-white text-xs rounded-sm"
                      data-testid="bom-copy-open-pair-button"
                    >
                      <Columns size={13} className="mr-1.5" />
                      {searching ? "Pulling from SAP..." : `Open selected side-by-side (${picked.length}/2)`}
                    </Button>
                  )}
                </div>
              )}

              {secondaryDraft && (
                <div className="bg-white border border-[#D0D5DD] rounded-sm p-2 flex flex-wrap items-center gap-2" data-testid="bom-copy-both-actions-bar">
                  <Columns size={16} className="text-[#175CD3]" />
                  <span className="text-[12px] font-semibold text-[#344054]">Editing 2 variants side-by-side</span>
                  <Button size="sm" onClick={validateBoth} className="h-7 bg-[#5925DC] hover:bg-[#4A1FB8] text-white text-xs rounded-sm ml-2" data-testid="bom-copy-validate-both-button">
                    Validate Both
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setConfirmTarget("both")}
                    disabled={eligibleForBoth.length === 0}
                    className="h-7 bg-[#B42318] hover:bg-[#912018] text-white text-xs rounded-sm font-bold"
                    data-testid="bom-copy-create-both-button"
                  >
                    Create Both in SAP
                  </Button>
                </div>
              )}

              <div className={secondaryDraft ? "grid grid-cols-1 xl:grid-cols-2 gap-4" : ""}>
                <VariantPanel
                  slot="primary"
                  panelLabel={secondaryDraft ? "Variant 1" : null}
                  draft={primaryDraft}
                  uiState={ui.primary}
                  onPatch={(patch) => patchDraft("primary", patch)}
                  onUpdateLine={(idx, patch) => updateLine("primary", idx, patch)}
                  onRemoveLine={(idx) => removeLine("primary", idx)}
                  onAddLine={() => addLine("primary")}
                  onSortLines={() => sortLinesByGroup("primary")}
                  onSave={() => saveDraft("primary")}
                  onValidate={() => validate("primary")}
                  onCreateRequest={() => setConfirmTarget("primary")}
                  onToggleComparison={() => patchUi("primary", { showComparison: !ui.primary.showComparison })}
                  onToggleAudit={() => loadAudit("primary")}
                />
                {secondaryDraft && (
                  <VariantPanel
                    slot="secondary"
                    panelLabel="Variant 2"
                    draft={secondaryDraft}
                    uiState={ui.secondary}
                    onPatch={(patch) => patchDraft("secondary", patch)}
                    onUpdateLine={(idx, patch) => updateLine("secondary", idx, patch)}
                    onRemoveLine={(idx) => removeLine("secondary", idx)}
                    onAddLine={() => addLine("secondary")}
                    onSortLines={() => sortLinesByGroup("secondary")}
                    onSave={() => saveDraft("secondary")}
                    onValidate={() => validate("secondary")}
                    onCreateRequest={() => setConfirmTarget("secondary")}
                    onToggleComparison={() => patchUi("secondary", { showComparison: !ui.secondary.showComparison })}
                    onToggleAudit={() => loadAudit("secondary")}
                    onCloseSecondary={() => setSecondaryDraft(null)}
                  />
                )}
              </div>

              {/* Sep 26 2026, P0 backlog item - Create New BOM in SAP is
                  irreversible (writes a real ECO + new BOM into SAP), so a
                  single misclick used to fire it immediately. Now it only
                  opens this confirm step; the real SAP call only happens
                  from the AlertDialogAction below. Also handles "Create
                  Both" (confirmTarget === "both") for the side-by-side flow. */}
              <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
                <AlertDialogContent data-testid="bom-copy-create-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{confirmInfo?.title}</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will create {confirmInfo?.boms.length === 1 ? "a real Engineering Change Order and new BOM" : "real Engineering Change Orders and new BOMs"}{" "}
                      <span className="font-semibold text-[#B42318]">{confirmInfo?.boms.join(", ")}</span> in SAP.
                      This action cannot be undone once submitted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bom-copy-create-confirm-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="bom-copy-create-confirm-submit"
                      className="bg-[#B42318] hover:bg-[#912018]"
                      onClick={handleConfirmCreate}
                    >
                      Yes, Create in SAP
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
